import { useEffect, useMemo } from "react";
import axios from "axios";
import { useSWRConfig } from "swr";
import useSWRInfinite from "swr/infinite";
import { getBlobFolderPage } from "../services/file-explorer-service";
import {
  BlobFolderPage,
  BlobFolderSummary,
  BlobInformation,
  UseFileExplorerFolderModel,
} from "../types";
import { toFolderSummary } from "../utils/folder-summary";
import { FILE_EXPLORER_KEY } from "./use-file-explorer";

const FOLDER_KEY = "/file-explorer/folders";

type FolderPageKey = [typeof FOLDER_KEY, string, string | null];

const isNotFoundError = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

// Loads a folder a section at a time. Large folders can hold 100,000+ files,
// so each section is fetched with the previous one's continuation token and
// kept as its own SWR cache entry. The next section is requested as soon as one
// arrives, so files show straight away while the rest load. When the last
// section arrives, its statistics for the whole folder replace the cached ones
// in the folder list, so the overview is up to date without refetching it.
// eslint-disable-next-line import/prefer-default-export
export const useFileExplorerFolder = (
  folderName: string | null,
): UseFileExplorerFolderModel => {
  const { mutate: mutateCache } = useSWRConfig();

  const updateFolderList = (
    update: (folders: BlobFolderSummary[]) => BlobFolderSummary[],
  ) => {
    mutateCache<BlobInformation>(
      FILE_EXPLORER_KEY,
      (current) =>
        current ? { ...current, folders: update(current.folders) } : current,
      { revalidate: false },
    );
  };

  const getKey = (
    pageIndex: number,
    previousPage: BlobFolderPage | null,
  ): FolderPageKey | null => {
    if (!folderName) return null;
    if (pageIndex === 0) return [FOLDER_KEY, folderName, null];
    if (!previousPage?.continuationToken) return null;
    return [FOLDER_KEY, folderName, previousPage.continuationToken];
  };

  const { data, error, size, setSize, mutate } = useSWRInfinite(
    getKey,
    ([, name, continuationToken]: FolderPageKey) =>
      getBlobFolderPage(name, continuationToken),
    {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      shouldRetryOnError: (pageError) => !isNotFoundError(pageError),
      onError: (pageError) => {
        if (isNotFoundError(pageError)) {
          updateFolderList((folders) =>
            folders.filter((f) => f.name !== folderName),
          );
        }
      },
    },
  );

  const pages = useMemo(() => data ?? [], [data]);
  const lastPage = pages.length > 0 ? pages[pages.length - 1] : undefined;
  const isComplete = !!lastPage && lastPage.continuationToken === null;

  // Carry on to the next section once the current one has arrived.
  useEffect(() => {
    if (lastPage?.continuationToken && !error && pages.length === size) {
      setSize(size + 1);
    }
  }, [lastPage, error, pages.length, size, setSize]);

  const statistics = isComplete ? lastPage.statistics : null;

  useEffect(() => {
    if (statistics && folderName) {
      updateFolderList((folders) =>
        folders.map((f) => (f.name === folderName ? { ...f, statistics } : f)),
      );
    }
    // updateFolderList only depends on the SWR cache, which doesn't change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statistics, folderName]);

  const blobs = useMemo(() => pages.flatMap((page) => page.blobs), [pages]);

  const isNotFound = isNotFoundError(error);
  const hasPages = pages.length > 0;

  return {
    folder:
      statistics && folderName
        ? toFolderSummary({ name: folderName, statistics })
        : undefined,
    blobs,
    isLoading: !!folderName && !hasPages && !error,
    isLoadingMore: hasPages && !isComplete && !error,
    isNotFound,
    error: isNotFound || hasPages ? undefined : error,
    loadMoreError: !isNotFound && hasPages ? error : undefined,
    retry: () => {
      mutate();
    },
    mutate: () => {
      mutate();
    },
  };
};
